import { contrastRatio } from "./mark-color.ts";

/**
 * The mark of a business with no logo: its initials on a colour. Pure, so the web, the mobile app
 * and the server draw the same mark for the same business.
 *
 * Latin letters only for now (decided 2026-10-04): digits and other scripts are skipped. A name
 * with no Latin letter (an Arabic name, for example) uses the business slug, which is always Latin.
 */

/** Eight colours, each with white text at 4.5:1 or more (the test checks it). */
export const BUSINESS_MARK_COLORS = [
  "#0F6E8C",
  "#7A3E9D",
  "#1F7A4C",
  "#B4462B",
  "#2E5A88",
  "#A3326B",
  "#4F5B19",
  "#8A5A00",
] as const;

/**
 * "Blue Fin Swim Academy" gives "BF", "Aquafit" gives "AQ". A name with no Latin letter falls back
 * to the slug ("al-noor" gives "AN"), and then to "?".
 */
export function businessInitials(name: string, slug?: string): string {
  const fromName = latinInitials(name);
  if (fromName) return fromName;
  return (slug ? latinInitials(slug.replace(/-/g, " ")) : null) ?? "?";
}

function latinInitials(text: string): string | null {
  const words = text
    .trim()
    .split(/\s+/)
    .map((word) => word.replace(/[^A-Za-z]/g, ""))
    .filter(Boolean);
  if (words.length === 0) return null;
  const [first, second] = words as [string, ...string[]];
  return (second ? `${first[0]}${second[0]}` : first.slice(0, 2)).toUpperCase();
}

/** The colour of a business's initials. The same business always gets the same colour. */
export function businessMarkColor(businessId: string): string {
  let hash = 0;
  for (const char of businessId) hash = (Math.imul(hash, 31) + char.charCodeAt(0)) >>> 0;
  return BUSINESS_MARK_COLORS[hash % BUSINESS_MARK_COLORS.length]!;
}

/** True when white text on `hex` passes WCAG AA. For the palette test and for callers that check. */
export function whiteTextPasses(hex: string): boolean {
  return contrastRatio(hex, "#FFFFFF") >= 4.5;
}
